import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import { setClassInvoiceDirectly } from '../../../requests/opsReports';
import {
  MATERIALS_STATUSES,
  PREP_CALL_STATUSES,
  TOpsClassDetailEx,
  TRAVEL_STATUSES,
} from '../../../requests/opsRegistrationsExtra';
import { Card, labelClass } from '../ui';
import { fieldClass, TClassForm, TSetField } from './shared';

type Props = {
  form: TClassForm;
  set: TSetField;
  id?: string;
  isNew: boolean;
  existing?: TOpsClassDetailEx;
};

/**
 * Prep call, customisation, materials and travel. The invoice-directly
 * checkbox saves on its own, outside the form.
 */
const DeliverySection: React.FC<Props> = ({
  form,
  set,
  id,
  isNew,
  existing,
}) => {
  const queryClient = useQueryClient();
  const invoiceFlag = useMutation({
    mutationFn: (v: boolean) => setClassInvoiceDirectly(id!, v),
    onSuccess: (r) => {
      showToast(
        r.invoiceDirectly
          ? 'Marked: invoice this class directly'
          : 'Invoice-directly override cleared',
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['opsClass', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card title="Delivery details">
      {!isNew && existing && (
        <div className="flex flex-wrap items-center gap-3 border-b border-white-regular px-5 py-3 text-sm">
          <label className="flex items-center gap-2 text-gray-four">
            <input
              type="checkbox"
              className="ops-check"
              checked={Boolean(
                (existing as { delivery?: { invoiceDirectly?: boolean } })
                  .delivery?.invoiceDirectly,
              )}
              disabled={invoiceFlag.isPending}
              onChange={(e) => invoiceFlag.mutate(e.target.checked)}
            />
            Invoice this class directly
          </label>
          <span className="text-xs text-gray-bold">
            {(
              existing as {
                delivery?: { invoiceDirectlySource?: string | null };
              }
            ).delivery?.invoiceDirectlySource === 'opportunity'
              ? 'Read from the deal’s payment type; tick to override.'
              : 'Sends it to the invoice queue under Reports.'}
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={labelClass}>Recording details</label>
          <input
            className={fieldClass}
            placeholder="Will it be recorded, by whom, where the file goes"
            value={form.recordingDetails}
            onChange={(e) => set('recordingDetails')(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Prep call status</label>
          <select
            className={fieldClass}
            value={form.prepCallStatus}
            onChange={(e) => set('prepCallStatus')(e.target.value)}
          >
            <option value="">— not set —</option>
            {PREP_CALL_STATUSES.map((st) => (
              <option key={st}>{st}</option>
            ))}
          </select>
          <div className="mt-1 text-xs text-gray-bold">
            {existing?.delivery?.prepCallSource === 'opportunity'
              ? `From the Salesforce opportunity (“${existing.delivery.opportunityPrepCallStatus}”); saving keeps the portal's own copy from here on.`
              : 'Needed or Requested puts a private class on the 14-day prep-call queue.'}
          </div>
        </div>
        <div>
          <label className={labelClass}>Customisation hours</label>
          <input
            type="number"
            min="0"
            step="0.5"
            className={fieldClass}
            placeholder="Development hours on this class"
            value={form.customisationHours}
            onChange={(e) => set('customisationHours')(e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Customisation notes</label>
          <textarea
            className={fieldClass}
            rows={3}
            placeholder="What was customised, by whom, how long it took"
            value={form.customisationNotes}
            onChange={(e) => set('customisationNotes')(e.target.value)}
          />
          {existing?.delivery?.opportunityCustomisationNotes && (
            <div className="mt-1 whitespace-pre-wrap text-xs text-gray-bold">
              On the opportunity:{' '}
              {existing.delivery.opportunityCustomisationNotes}
            </div>
          )}
        </div>
        <div>
          <label className={labelClass}>Materials shipped</label>
          <select
            className={fieldClass}
            value={form.materialsStatus}
            onChange={(e) => set('materialsStatus')(e.target.value)}
          >
            <option value="">— not set —</option>
            {MATERIALS_STATUSES.map((st) => (
              <option key={st}>{st}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Shipping / tracking note</label>
          <input
            className={fieldClass}
            placeholder="Carrier, tracking number, what went"
            value={form.materialsNote}
            onChange={(e) => set('materialsNote')(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Travel status</label>
          <select
            className={fieldClass}
            value={form.travelStatus}
            onChange={(e) => set('travelStatus')(e.target.value)}
          >
            <option value="">— not set —</option>
            {TRAVEL_STATUSES.map((st) => (
              <option key={st}>{st}</option>
            ))}
          </select>
          <div className="mt-1 text-xs text-gray-bold">
            An onsite class marked Required goes on the 45-day travel sweep
            until it is Booked.
          </div>
        </div>
        <div>
          <label className={labelClass}>Travel payee</label>
          <input
            className={fieldClass}
            placeholder="Who is paid for the travel"
            value={form.travelPayee}
            onChange={(e) => set('travelPayee')(e.target.value)}
          />
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-five">
            <input
              type="checkbox"
              className="ops-check h-4 w-4 accent-blue-primary"
              checked={form.invoiceCustomerForTravel}
              onChange={(e) =>
                set('invoiceCustomerForTravel')(e.target.checked)
              }
            />
            Invoice the customer for travel
          </label>
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Travel notes</label>
          <textarea
            className={fieldClass}
            rows={3}
            placeholder="Itinerary, hotel, who booked what"
            value={form.travelNotes}
            onChange={(e) => set('travelNotes')(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 md:col-span-2">
          {(
            [
              ['confirmOutline', 'Outline confirmed'],
              ['confirmProjectNotes', 'Project notes confirmed'],
              ['productMatchesOutline', 'Product matches the outline'],
            ] as const
          ).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-2 text-sm text-gray-five"
            >
              <input
                type="checkbox"
                className="ops-check h-4 w-4 accent-blue-primary"
                checked={form[key]}
                onChange={(e) => set(key)(e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="text-xs text-gray-bold md:col-span-2">
          Travel notes, payee, the invoice checkbox and the three confirmations
          are Salesforce fields on the class. Prep call status, customisation
          hours and notes, materials and travel status are kept by the portal —
          Salesforce has no field for them on the class (prep call status lives
          on the opportunity there).
        </div>
      </div>
    </Card>
  );
};

export default DeliverySection;
